import type { Metadata } from "@/lib/head";
import { Link, useRouter } from "@/lib/nav";
import { ChevronLeft, ChevronRight, CircleCheck, ImagePlus, LoaderCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import Field from "@/components/field";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Textarea } from "@buildpy/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@buildpy/ui/sheet";
import { formatDate, formatDateTime } from "@buildpy/core/lib/format";
import type { CustomerAsRequest } from "@buildpy/core/modules/as-request/rules";
import { customerAsPhotoPath, customerHousePath, customerProjectPath } from "@buildpy/core/modules/project/customer-visible";
import { photoSizeBlockReason } from "@buildpy/core/modules/project/photo";
import type { CustomerAsView } from "@buildpy/core/views/g3b/customer-as";
import { submitAsRequestByLink } from "@/api/actions/as-request-share";
import { verifyContractShare } from "@/api/actions/contract-share";
import { settleRedirect } from "@/lib/redirect-error";
import { AS_STATUS_TONE } from "@/modules/as-request/ui/status-tone";
import UnusableLink from "@/modules/contract/ui/unusable-link";
import CustomerPhotoThumb from "@/modules/project/ui/customer-photo-thumb";
import { resizeImage } from "@/modules/project/ui/resize-image";
import ShareGate from "@/modules/share/ui/share-gate";

export const metadata: Metadata = {
  title: "A/S 접수",
  robots: { index: false, follow: false },
};

interface Props {
  token: string;
  data: CustomerAsView;
}

type Ok = Extract<CustomerAsView, { kind: "ok" }>;

interface Draft {
  category: string;
  body: string;
  photos: number;
}

const FAILED = "A/S 를 접수하지 못했습니다. 잠시 뒤 다시 보내 주세요";
const UNREADABLE = "사진을 열 수 없습니다";

// 서버 검사와 같은 코드포인트 기준으로 센다.
const lengthOf = (value: string): number => Array.from(value).length;

// 접수 번호(uuid v4) — 화면을 열 때 한 번 만들어 다시 보내도 같은 번호로 접수가 한 건만 남는다.
export const newRequestId = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = Array.from(b, (n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// 더 넣을 수 있는 장수 — 줄이는 중인 사진도 자리를 차지한다.
export const photoRoomOf = (max: number, picked: number, resizing: number): number => Math.max(0, max - picked - resizing);

const Notice = ({ text }: { text: string }) => (
  <CustomerScreenShell>
    <SectionCard as="div" className="mx-auto w-full max-w-sm p-5 text-center text-sm" padding="none" role="alert">
      {text}
    </SectionCard>
  </CustomerScreenShell>
);

const Header = ({ token }: { token: string }) => (
  <header className="pt-safe sticky top-0 z-10 border-b border-border bg-card">
    <div className="flex items-center gap-x-1 px-2 py-1.5">
      <Button asChild aria-label="공사 첫 화면으로" size="icon" variant="ghost">
        <Link data-testid="customer-as-back" href={customerProjectPath(token)}>
          <ChevronLeft />
        </Link>
      </Button>
      <h1 className="text-base font-semibold">A/S 접수</h1>
    </div>
  </header>
);

interface Picked {
  blob: Blob;
  url: string;
}

// 고른 사진 미리보기 주소 — 빼거나 화면을 떠나면 놓아 준다.
const usePicked = () => {
  const [picked, setPicked] = useState<Picked[]>([]);
  const ref = useRef<Picked[]>([]);
  ref.current = picked;
  useEffect(() => () => ref.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  const add = (blob: Blob) => setPicked((list) => [...list, { blob, url: URL.createObjectURL(blob) }]);
  const remove = (index: number) =>
    setPicked((list) => {
      const gone = list[index];
      if (gone) URL.revokeObjectURL(gone.url);
      return list.filter((_, i) => i !== index);
    });
  const clear = () =>
    setPicked((list) => {
      list.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  return { picked, add, remove, clear };
};

const AsForm = ({ token, data, onDone }: { token: string; data: Ok; onDone: (draft: Draft) => void }) => {
  const router = useRouter();
  const [category, setCategory] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [requestId] = useState(newRequestId);
  const [resize, setResize] = useState<{ done: number; total: number } | null>(null);
  const { picked, add, remove, clear } = usePicked();
  const fileRef = useRef<HTMLInputElement>(null);
  const length = lengthOf(body);
  const resizing = resize ? resize.total - resize.done : 0;
  const full = photoRoomOf(data.maxPhotos, picked.length, resizing) === 0;
  const ready = category !== null && body.trim() !== "" && length <= data.maxBody;

  const attach = async (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (fileRef.current) fileRef.current.value = "";
    setError(null);
    const take = files.slice(0, photoRoomOf(data.maxPhotos, picked.length, resizing));
    const over = files.length > take.length ? `사진은 ${data.maxPhotos}장까지 넣을 수 있어요` : null;
    if (take.length === 0) {
      setError(over);
      return;
    }
    setResize({ done: 0, total: take.length });
    try {
      for (const [index, file] of take.entries()) {
        let blob: Blob;
        try {
          blob = await resizeImage(file);
        } catch {
          setError(UNREADABLE);
          return;
        }
        // 줄인 뒤에도 크면 서버 한도에 막히기 전에 여기서 알린다.
        const sizeReason = photoSizeBlockReason(blob.size);
        if (sizeReason) {
          setError(sizeReason);
          return;
        }
        add(blob);
        setResize({ done: index + 1, total: take.length });
      }
      setError(over);
    } finally {
      setResize(null);
    }
  };

  const submit = async () => {
    if (!ready || category === null || resizing > 0) return;
    setPending(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("id", requestId);
      form.append("category", category);
      form.append("body", body);
      for (const p of picked) form.append("file", p.blob, "photo.jpg");
      const result = await submitAsRequestByLink(token, form);
      if (!result.ok) {
        setConfirm(false);
        setError(result.message);
        // 입장 풀림·링크 막힘·대상 거절이면 다시 읽어 지금 상태(입장 문·안내)를 그린다.
        if (result.reverify || result.locked || result.rejected) router.refresh();
        return;
      }
      const draft = { category, body, photos: picked.length };
      setConfirm(false);
      setCategory(null);
      setBody("");
      clear();
      router.refresh();
      onDone(draft);
    } catch (e) {
      if (settleRedirect(e)) return;
      setConfirm(false);
      setError(FAILED);
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <SectionCard className="flex flex-col gap-y-4" data-testid="customer-as-form">
        <div className="flex flex-col gap-y-1.5">
          <span className="text-sm font-medium" id="customer-as-category-label">
            어디가 불편하세요
          </span>
          <div aria-labelledby="customer-as-category-label" className="flex flex-wrap gap-1.5" role="radiogroup">
            {data.categories.map((c) => (
              <Button
                key={c}
                aria-checked={category === c}
                disabled={pending}
                role="radio"
                size="chip"
                type="button"
                variant="chip"
                onClick={() => setCategory(c)}>
                {c}
              </Button>
            ))}
          </div>
        </div>

        <Field label="어떤 문제인가요">
          <Textarea
            aria-label="증상"
            className="min-h-28 text-base"
            data-testid="customer-as-body"
            disabled={pending}
            placeholder="예: 욕실 줄눈 한쪽이 갈라졌어요"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        {length > data.maxBody && (
          <p className="-mt-3 text-xs text-destructive">
            {length}/{data.maxBody}자 — 줄여 주세요
          </p>
        )}

        <div className="flex flex-col gap-y-2" data-testid="customer-as-photos">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">사진</span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {picked.length}/{data.maxPhotos}
            </span>
          </div>
          {picked.length > 0 && (
            <ul className="grid grid-cols-3 gap-1">
              {picked.map((p, i) => (
                <li key={p.url} className="relative">
                  <img alt={`넣은 사진 ${i + 1}`} className="aspect-square w-full rounded-md object-cover" src={p.url} />
                  <Button
                    aria-label={`사진 ${i + 1} 빼기`}
                    className="absolute right-1 top-1"
                    disabled={pending}
                    size="icon-xs"
                    type="button"
                    variant="outline"
                    onClick={() => remove(i)}>
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Button
            className="self-start"
            data-testid="customer-as-attach"
            disabled={pending || full || resizing > 0}
            size="sm"
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}>
            <ImagePlus />
            사진 넣기
          </Button>
          {resize ? (
            <p aria-live="polite" className="flex items-center gap-x-1.5 text-xs text-muted-foreground" data-testid="customer-as-resizing" role="status">
              <LoaderCircle aria-hidden className="size-3.5 animate-spin" />
              사진 준비 중 {Math.min(resize.done + 1, resize.total)}/{resize.total}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">사진이 있으면 더 빨리 확인할 수 있어요</p>
          )}
          <input
            ref={fileRef}
            multiple
            accept="image/*"
            aria-label="A/S 사진 고르기"
            className="hidden"
            type="file"
            onChange={(event) => void attach(event.target.files)}
          />
        </div>

        {error && (
          <p className="text-sm text-destructive" data-testid="customer-as-error" role="alert">
            {error}
          </p>
        )}
      </SectionCard>

      <div className="pb-safe-2 fixed inset-x-0 bottom-0 z-10 border-t border-border bg-card px-3 pt-2">
        <div className="mx-auto w-full max-w-md">
          <Button
            className="w-full"
            data-testid="customer-as-submit"
            disabled={!ready || pending || resizing > 0}
            size="cta"
            type="button"
            variant="primary"
            onClick={() => setConfirm(true)}>
            A/S 접수하기
          </Button>
        </div>
      </div>

      <Sheet open={confirm} onOpenChange={(next) => !pending && setConfirm(next)}>
        <SheetContent className="mx-auto flex max-w-md flex-col gap-y-4 p-5" data-testid="customer-as-confirm" side="bottom">
          <SheetHeader className="flex-col items-start gap-y-1 p-0">
            <SheetTitle>이대로 접수할까요</SheetTitle>
            <SheetDescription>비용이 드는지는 담당자가 확인하고 안내해요</SheetDescription>
          </SheetHeader>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">위치</dt>
            <dd>{category}</dd>
            <dt className="text-muted-foreground">내용</dt>
            <dd className="line-clamp-3 whitespace-pre-wrap break-words">{body}</dd>
            <dt className="text-muted-foreground">사진</dt>
            <dd>{picked.length > 0 ? `사진 ${picked.length}장` : "없음"}</dd>
          </dl>
          <div className="flex flex-col gap-y-2 pb-safe">
            <Button data-testid="customer-as-confirm-submit" disabled={pending || resizing > 0} size="lg" variant="primary" onClick={() => void submit()}>
              {pending ? "보내는 중" : "접수하기"}
            </Button>
            <Button disabled={pending} size="lg" variant="ghost-muted" onClick={() => setConfirm(false)}>
              고칠게요
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const Done = ({ draft, onMore }: { draft: Draft; onMore: () => void }) => (
  <SectionCard className="flex flex-col items-center gap-y-3 text-center" data-testid="customer-as-done" role="status">
    <CircleCheck aria-hidden className="size-10 text-primary" />
    <div className="flex flex-col gap-y-1">
      <p className="text-base font-semibold">A/S 를 접수했어요</p>
      <p className="text-sm text-muted-foreground">담당자가 확인하고 연락드려요</p>
    </div>
    <dl className="grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-left text-sm">
      <dt className="text-muted-foreground">위치</dt>
      <dd>{draft.category}</dd>
      <dt className="text-muted-foreground">내용</dt>
      <dd className="line-clamp-3 whitespace-pre-wrap break-words">{draft.body}</dd>
      {draft.photos > 0 && (
        <>
          <dt className="text-muted-foreground">사진</dt>
          <dd>사진 {draft.photos}장</dd>
        </>
      )}
    </dl>
    <Button className="w-full" data-testid="customer-as-more" size="lg" variant="outline" onClick={onMore}>
      하나 더 접수하기
    </Button>
  </SectionCard>
);

const RequestItem = ({ token, request }: { token: string; request: CustomerAsRequest }) => (
  <SectionCard as="li" className="flex flex-col gap-y-2 text-sm" data-testid="customer-as-item">
    <div className="flex items-center justify-between gap-2">
      <span className="font-semibold">{request.category}</span>
      <Pill tone={AS_STATUS_TONE[request.status]}>{request.status_label}</Pill>
    </div>
    <p className="whitespace-pre-wrap break-words">{request.body}</p>
    {request.status === "SCHEDULED" && request.scheduled_at && (
      <p className="font-medium" data-testid="customer-as-scheduled">
        방문 예정 {formatDateTime(request.scheduled_at)}
      </p>
    )}
    {request.photo_count > 0 && (
      <ul className="grid grid-cols-4 gap-1">
        {Array.from({ length: request.photo_count }, (_, i) => (
          <li key={i}>
            <CustomerPhotoThumb alt={`A/S 사진 ${i + 1}`} src={customerAsPhotoPath(token, request.id, i)} />
          </li>
        ))}
      </ul>
    )}
    <p className="text-xs text-muted-foreground">접수 {formatDate(request.created_at)}</p>
  </SectionCard>
);

// 고객 A/S 접수 — 「내 현장」 과 같은 입장(링크 + 입장 쿠키) · 위치·내용·사진 → 확인 시트 → 접수 · 아래에 내 접수 목록.
const Page = ({ token, data }: Props) => {
  const [done, setDone] = useState<Draft | null>(null);
  if (data.kind === "dead") return <UnusableLink token={token} />;
  if (data.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate docName="A/S 접수" orgName={data.orgName} token={token} verify={verifyContractShare} />
      </CustomerScreenShell>
    );
  }
  if (data.kind === "notice") return <Notice text={data.text} />;

  return (
    <CustomerScreenShell className={done ? "p-0 pb-10" : "p-0 pb-28"}>
      <Header token={token} />
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4 px-4 pt-4">
        <div className="flex flex-col gap-y-0.5">
          <p className="text-sm text-muted-foreground">{data.orgName}</p>
          <p className="text-base font-semibold">공사 후 불편한 곳을 알려 주세요</p>
        </div>

        {done ? <Done draft={done} onMore={() => setDone(null)} /> : <AsForm data={data} token={token} onDone={setDone} />}

        <section className="flex flex-col gap-y-2" data-testid="customer-as-list">
          <h2 className="text-base font-semibold">내 A/S</h2>
          {data.requests.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="customer-as-empty">
              아직 접수한 A/S 가 없어요.
            </p>
          ) : (
            <ul className="flex flex-col gap-y-2">
              {data.requests.map((r) => (
                <RequestItem key={r.id} request={r} token={token} />
              ))}
            </ul>
          )}
        </section>

        <Link
          className="flex items-center justify-between gap-2 rounded-md border border-border px-4 py-3 text-sm hover:bg-muted"
          data-testid="customer-as-house-link"
          href={customerHousePath(token)}>
          <span className="flex flex-col">
            <span className="font-medium">우리 집 기록</span>
            <span className="text-xs text-muted-foreground">공간별 시공 내역과 보증 기간</span>
          </span>
          <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
        </Link>
      </div>
    </CustomerScreenShell>
  );
};

export default Page;
